import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  FiRefreshCw,
  FiSearch,
  FiSmartphone,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { adminService } from "../../services/adminService";
import { GlassCard } from "../../components/ui/GlassCard";
import { format } from "date-fns";

export const AdminVirtualNumberPurchasesPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadPurchases = async (showSpinner = false) => {
    try {
      if (showSpinner) setRefreshing(true);
      else setLoading(true);
      const response = await adminService.getVirtualNumberPurchases();
      setPurchases(response.purchases || []);
    } catch (error) {
      console.error("Failed to load virtual number purchases", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadPurchases();
  }, []);

  const filteredPurchases = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return purchases;

    return purchases.filter((purchase) => {
      const userName = purchase.user?.username || "";
      const email = purchase.user?.email || "";
      const phoneNumber = purchase.phoneNumber || "";
      const serviceName = purchase.serviceName || "";
      const status = purchase.status || "";
      return [userName, email, phoneNumber, serviceName, status]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [purchases, search]);

  const getStatusBadge = (status) => {
    const normalized = String(status || "").toLowerCase();
    switch (normalized) {
      case "active":
        return (
          <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700">
            Active
          </span>
        );
      case "completed":
        return (
          <span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700">
            Completed
          </span>
        );
      case "expired":
        return (
          <span className="px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-700">
            Expired
          </span>
        );
      case "cancelled":
        return (
          <span className="px-3 py-1 text-sm rounded-full bg-red-100 text-red-700">
            Cancelled
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700">
            {status || "Unknown"}
          </span>
        );
    }
  };

  return (
    <div className="p-6 md:p-8">
      <Helmet>
        <title>Virtual Number Purchases | Admin</title>
      </Helmet>

      <div className="flex flex-col md:flex-row justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Virtual Number Purchases
          </h1>
          <p className="text-gray-600">
            See every purchased number, the buyer, purchase time, current
            status, and received code.
          </p>
        </div>
        <button
          onClick={() => loadPurchases(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-3 text-white font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing..." : "Refresh statuses"}
        </button>
      </div>

      <GlassCard className="p-4 mb-6">
        <div className="relative max-w-xl">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by buyer, phone, service, or status"
            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </GlassCard>

      {loading ? (
        <GlassCard className="p-10 text-center text-gray-600">
          Loading virtual number purchases...
        </GlassCard>
      ) : filteredPurchases.length === 0 ? (
        <GlassCard className="p-10 text-center text-gray-600">
          No virtual number purchases found.
        </GlassCard>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Buyer
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Service
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Number
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Purchased At
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Balance Before
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Balance After
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Refund
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Balance After Refund
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Code
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredPurchases.map((purchase) => (
                  <tr key={purchase._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        {purchase.user?.username || "Unknown user"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {purchase.user?.email || "No email"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        {purchase.serviceName || "N/A"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {purchase.countryName || ""}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 font-medium text-gray-900">
                        <FiSmartphone className="text-primary-600" />
                        {purchase.phoneNumber || "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <FiClock className="text-gray-400" />
                        {purchase.createdAt
                          ? format(
                              new Date(purchase.createdAt),
                              "MMM dd, yyyy HH:mm",
                            )
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        ₦{purchase.sellingPrice?.toLocaleString() || "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        ₦
                        {purchase.balanceBeforePurchase?.toLocaleString() ||
                          "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div
                        className={`font-medium ${purchase.balanceAfterPurchase < 0 ? "text-red-600" : "text-gray-900"}`}
                      >
                        ₦
                        {purchase.balanceAfterPurchase?.toLocaleString() ||
                          "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {purchase.refundAmount > 0 ? (
                        <div className="space-y-1">
                          <div className="font-medium text-green-700">
                            ₦{purchase.refundAmount?.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {purchase.refundedAt
                              ? format(
                                  new Date(purchase.refundedAt),
                                  "MMM dd, HH:mm",
                                )
                              : "Pending"}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">No refund</div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        ₦
                        {purchase.balanceAfterRefund?.toLocaleString() || "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(purchase.status)}
                    </td>
                    <td className="px-4 py-4">
                      {purchase.smsCode ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
                            <FiCheckCircle />
                            {purchase.smsCode}
                          </div>
                          {purchase.smsText && (
                            <div className="text-xs text-gray-500">
                              {purchase.smsText}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <FiAlertCircle />
                          No code received yet
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
